import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  GetMaintenanceTasksAPI,
  GetMaintenanceCountersAPI,
  GetMaintenanceTaskByIdAPI,
  CreateMaintenanceTaskAPI,
  UpdateMaintenanceTaskAPI,
  DeleteMaintenanceTaskAPI,
  UpdateMaintenanceTaskStatusAPI,
  UpdateMaintenanceBudgetAPI,
} from "../../api/maintenance";

/* ============================
   GET TASKS
============================ */

export const getMaintenanceTasks = createAsyncThunk(
  "maintenance/getTasks",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await GetMaintenanceTasksAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch maintenance tasks");
    }
  }
);

/* ============================
   GET COUNTERS
============================ */

export const getMaintenanceCounters = createAsyncThunk(
  "maintenance/getCounters",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await GetMaintenanceCountersAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch maintenance counters"
      );
    }
  }
);

/* ============================
   GET TASK DETAILS
============================ */

export const getMaintenanceTaskById = createAsyncThunk(
  "maintenance/getTaskById",
  async (taskId, { rejectWithValue }) => {
    try {
      const { data } = await GetMaintenanceTaskByIdAPI(taskId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch maintenance task");
    }
  }
);

/* ============================
   CREATE TASK
============================ */

export const createMaintenanceTask = createAsyncThunk(
  "maintenance/createTask",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await CreateMaintenanceTaskAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create maintenance task");
    }
  }
);

/* ============================
   UPDATE TASK
============================ */

export const updateMaintenanceTask = createAsyncThunk(
  "maintenance/updateTask",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await UpdateMaintenanceTaskAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update maintenance task");
    }
  }
);

/* ============================
   DELETE TASK
============================ */

export const deleteMaintenanceTask = createAsyncThunk(
  "maintenance/deleteTask",
  async (taskId, { rejectWithValue }) => {
    try {
      const { data } = await DeleteMaintenanceTaskAPI(taskId);

      return {
        apiRes: data,
        taskId,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete maintenance task");
    }
  }
);

/* ============================
   UPDATE STATUS
============================ */

export const updateMaintenanceTaskStatus = createAsyncThunk(
  "maintenance/updateTaskStatus",
  async ({ taskId, status }, { rejectWithValue }) => {
    try {
      const { data } = await UpdateMaintenanceTaskStatusAPI({
        taskId,
        status,
      });

      return {
        ...data,
        taskId,
        status,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update task status");
    }
  }
);

/* ============================
   UPDATE BUDGET
============================ */

export const updateMaintenanceBudget = createAsyncThunk(
  "maintenance/updateBudget",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await UpdateMaintenanceBudgetAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update budget");
    }
  }
);

const initialState = {
  tasks: [],
  selectedTask: null,
  taskActivity: [],

  maintenanceCounters: null,

  tasksLoading: false,
  tasksError: null,

  selectedTaskLoading: false,
  selectedTaskError: null,

  maintenanceCountersLoading: false,
  maintenanceCountersError: null,

  createTaskLoading: false,
  createTaskError: null,
  createTaskSuccess: false,

  updateTaskLoading: false,
  updateTaskError: null,
  updateTaskSuccess: false,

  deleteTaskLoading: false,
  deleteTaskError: null,
  deleteTaskSuccess: false,

  updateTaskStatusLoading: false,
  updateTaskStatusError: null,
  updateTaskStatusSuccess: false,

  updateBudgetLoading: false,
  updateBudgetError: null,
  updateBudgetSuccess: false,

  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  },
};

const maintenanceSlice = createSlice({
  name: "maintenance",

  initialState,

  reducers: {
    clearSelectedTask: (state) => {
      state.selectedTask = null;
      state.taskActivity = [];
    },

    resetCreateTaskState: (state) => {
      state.createTaskLoading = false;
      state.createTaskError = null;
      state.createTaskSuccess = false;
    },

    resetUpdateTaskState: (state) => {
      state.updateTaskLoading = false;
      state.updateTaskError = null;
      state.updateTaskSuccess = false;
      state.selectedTask = null;
    },

    resetDeleteTaskState: (state) => {
      state.deleteTaskLoading = false;
      state.deleteTaskError = null;
      state.deleteTaskSuccess = false;
    },

    resetTaskStatusState: (state) => {
      state.updateTaskStatusLoading = false;
      state.updateTaskStatusError = null;
      state.updateTaskStatusSuccess = false;
    },

    resetBudgetState: (state) => {
      state.updateBudgetLoading = false;
      state.updateBudgetError = null;
      state.updateBudgetSuccess = false;
    },

    resetMaintenanceTaskState: (state) => {
      state.createTaskLoading = false;
      state.createTaskError = null;
      state.createTaskSuccess = false;

      state.updateTaskLoading = false;
      state.updateTaskError = null;
      state.updateTaskSuccess = false;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ============================
         GET TASKS
      ============================ */

      .addCase(getMaintenanceTasks.pending, (state) => {
        state.tasksLoading = true;
      })

      .addCase(getMaintenanceTasks.fulfilled, (state, action) => {
        state.tasksLoading = false;
        state.tasksError = null;

        state.tasks = action.payload.data?.tasks || [];

        state.pagination = action.payload.data?.pagination || initialState.pagination;
      })

      .addCase(getMaintenanceTasks.rejected, (state, action) => {
        state.tasksLoading = false;
        state.tasksError = action.payload;
      })

      /* ============================
         GET COUNTERS
      ============================ */

      .addCase(getMaintenanceCounters.pending, (state) => {
        state.maintenanceCountersLoading = true;
      })

      .addCase(getMaintenanceCounters.fulfilled, (state, action) => {
        state.maintenanceCountersLoading = false;
        state.maintenanceCountersError = null;

        state.maintenanceCounters = action.payload.data.counters || null;
      })

      .addCase(getMaintenanceCounters.rejected, (state, action) => {
        state.maintenanceCountersLoading = false;
        state.maintenanceCountersError = action.payload;
      })

      /* ============================
         GET DETAILS
      ============================ */

      .addCase(getMaintenanceTaskById.pending, (state) => {
        state.selectedTaskLoading = true;
      })

      .addCase(getMaintenanceTaskById.fulfilled, (state, action) => {
        state.selectedTaskLoading = false;
        state.selectedTaskError = null;

        state.selectedTask = action.payload.data?.task || action.payload.data || null;

        state.taskActivity = action.payload.data?.activity || [];
      })

      .addCase(getMaintenanceTaskById.rejected, (state, action) => {
        state.selectedTaskLoading = false;
        state.selectedTaskError = action.payload;
      })

      /* ============================
         CREATE
      ============================ */

      .addCase(createMaintenanceTask.pending, (state) => {
        state.createTaskLoading = true;
        state.createTaskError = null;
        state.createTaskSuccess = false;
      })

      .addCase(createMaintenanceTask.fulfilled, (state, action) => {
        state.createTaskLoading = false;
        state.createTaskSuccess = true;

        const task = action.payload.data?.task || action.payload.data;

        if (task) {
          state.tasks.unshift(task);
        }
      })

      .addCase(createMaintenanceTask.rejected, (state, action) => {
        state.createTaskLoading = false;
        state.createTaskError = action.payload;
      })

      /* ============================
         UPDATE
      ============================ */

      .addCase(updateMaintenanceTask.pending, (state) => {
        state.updateTaskLoading = true;
        state.updateTaskError = null;
        state.updateTaskSuccess = false;
      })

      .addCase(updateMaintenanceTask.fulfilled, (state, action) => {
        state.updateTaskLoading = false;
        state.updateTaskSuccess = true;

        const updatedTask = action.payload.data?.task || action.payload.data;

        if (!updatedTask?._id) return;

        state.tasks = state.tasks.map((task) =>
          task._id === updatedTask._id ? updatedTask : task
        );

        if (state.selectedTask?._id === updatedTask._id) {
          state.selectedTask = updatedTask;
        }
      })

      .addCase(updateMaintenanceTask.rejected, (state, action) => {
        state.updateTaskLoading = false;
        state.updateTaskError = action.payload;
      })

      /* ============================
         DELETE
      ============================ */

      .addCase(deleteMaintenanceTask.pending, (state) => {
        state.deleteTaskLoading = true;
        state.deleteTaskError = null;
        state.deleteTaskSuccess = false;
      })

      .addCase(deleteMaintenanceTask.fulfilled, (state, action) => {
        state.deleteTaskLoading = false;
        state.deleteTaskSuccess = true;

        const { taskId } = action.payload;

        state.tasks = state.tasks.filter((task) => task._id !== taskId);
      })

      .addCase(deleteMaintenanceTask.rejected, (state, action) => {
        state.deleteTaskLoading = false;
        state.deleteTaskError = action.payload;
      })

      /* ============================
         STATUS UPDATE
      ============================ */

      .addCase(updateMaintenanceTaskStatus.pending, (state) => {
        state.updateTaskStatusLoading = true;
        state.updateTaskStatusError = null;
        state.updateTaskStatusSuccess = false;
      })

      .addCase(updateMaintenanceTaskStatus.fulfilled, (state, action) => {
        state.updateTaskStatusLoading = false;
        state.updateTaskStatusSuccess = true;

        const { taskId, status } = action.payload;

        const task = state.tasks.find((item) => item._id === taskId);

        if (task) {
          task.status = status;
        }

        if (state.selectedTask?._id === taskId) {
          state.selectedTask.status = status;
        }
      })

      .addCase(updateMaintenanceTaskStatus.rejected, (state, action) => {
        state.updateTaskStatusLoading = false;
        state.updateTaskStatusError = action.payload;
      })

      /* ============================
         BUDGET
      ============================ */

      .addCase(updateMaintenanceBudget.pending, (state) => {
        state.updateBudgetLoading = true;
        state.updateBudgetError = null;
        state.updateBudgetSuccess = false;
      })

      .addCase(updateMaintenanceBudget.fulfilled, (state) => {
        state.updateBudgetLoading = false;
        state.updateBudgetSuccess = true;
      })

      .addCase(updateMaintenanceBudget.rejected, (state, action) => {
        state.updateBudgetLoading = false;
        state.updateBudgetError = action.payload;
      });
  },
});

export const {
  clearSelectedTask,
  resetCreateTaskState,
  resetUpdateTaskState,
  resetDeleteTaskState,
  resetTaskStatusState,
  resetBudgetState,
  resetMaintenanceTaskState,
} = maintenanceSlice.actions;

export default maintenanceSlice.reducer;
